import React, { useState, useEffect, useContext } from "react";
import ProductSearchModal from "../components/Modals/ProductSearchModal";
import ProductInfoModal from "../components/Modals/ProductInfoModal";
import { FaShoppingCart, FaSearch, FaArrowLeft, FaInfoCircle, FaCommentDots, FaTrash, FaPencilAlt } from "react-icons/fa";
import { CartContext } from "../context/CartContext";
import { useHistory, useLocation } from "react-router-dom";
import { buildOrderPayload } from "../utils/buildOrderPayload";
import { handleBulkPaste } from "../utils/handleBulkPaste";
import { API_BASE } from "../utils/api";
import { hasAccountAccess } from "../utils/permissions";
import { getMyAccountId, isBMI } from "../utils/config";
import { useLanguage } from "../context/LanguageContext";

import Unauthorized from "./Unauthorized"; // ajusta la ruta si es necesario

// Evita el error de redondeo IEEE 754: 5.1975 * 10 = 51.97499... en float
const calcCents = (price, qty) => Math.round(Math.round(price * 10000) * qty / 100);

export default function OrderExpress() {
  const MYACCOUNT_ID = getMyAccountId();
  const history = useHistory();
  const location = useLocation();
  const [isProformaMode, setIsProformaMode] = useState(
    () => localStorage.getItem("order_mode") === "proforma"
      || new URLSearchParams(location.search).get("mode") === "proforma"
  );
  useEffect(() => {
    const sync = () => setIsProformaMode(localStorage.getItem("order_mode") === "proforma");
    window.addEventListener("orderModeChange", sync);
    return () => window.removeEventListener("orderModeChange", sync);
  }, []);

  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [showEmptyConfirm, setShowEmptyConfirm] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  // ====================== ESTADOS PRINCIPALES ======================
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNoteIdx, setEditingNoteIdx] = useState(null);
  const [editingNoteValue, setEditingNoteValue] = useState("");
  const user = JSON.parse(localStorage.getItem("user"));
  const { clearCart, cartItems: items, setCartItems } = useContext(CartContext);
  const setItems = setCartItems;
  const { t, switchLanguage, language } = useLanguage();

  // ====================== CARGA INICIAL ======================
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };


  // ====================== FILA DE INGRESO ======================
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("—");
  const [qty, setQty] = useState(1);
  const [idProduct, setIdProduct] = useState(null);
  const [price, setPrice] = useState("—");
  const [subtotal, setSubtotal] = useState("—");
  const [taxLine, setTaxLine] = useState("0.00");
  const [imgPreview, setImgPreview] = useState(null);
  const [payTaxFlag, setPayTaxFlag] = useState(false);
  const [serviceFlag, setServiceFlag] = useState(0);
  const [bulkErrors, setBulkErrors] = useState([]);

  // ====================== MODAL INFO ======================
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoProduct, setInfoProduct] = useState(null);

  // ====================== RECALCULAR SUBTOTAL ======================
  useEffect(() => {
    if (!isNaN(price) && !isNaN(qty) && price !== "—") {
      const subtotalCents = calcCents(price, qty);
      const subtotalFinal = (subtotalCents / 100).toFixed(2);

      const iva = payTaxFlag
        ? (Math.round(subtotalCents * (user.porcentaje / 100)) / 100).toFixed(2)
        : "0.00";

      setSubtotal(subtotalFinal);
      setTaxLine(iva);
    }
  }, [qty, price, payTaxFlag, user.porcentaje]);

  // ====================== ACTUALIZAR DESDE MODAL ======================
  const updateItemFromModal = (newQty, newSubtotal) => {
    const i = infoProduct.index;

    const updated = items.map((x, idx) => {
      if (idx === i) {
        // 1️⃣ Aseguramos valores numéricos limpios
        const price = Number(x.price || 0);
        const porcentaje = Number(user?.porcentaje || 0) / 100;
        const qty = Number(newQty);

        const subtotalCents = calcCents(price, qty);
        const subtotalStr = (subtotalCents / 100).toFixed(2);

        const tax = x.payTax
          ? (Math.round(subtotalCents * porcentaje) / 100).toFixed(2)
          : "0.00";

        return {
          ...x,
          qty: qty,
          subtotal: subtotalStr, // Reemplaza al newSubtotal del modal para asegurar consistencia
          tax: tax,
        };
      }
      return x;
    });

    setItems(updated);
  };

  // ====================== BUSCAR POR CÓDIGO ======================
  const findProductByCode = async () => {
    if (!code.trim()) return;

    try {
      const res = await fetch(
        `${API_BASE}/inventary-ultra/api/ws/search_products_api?myaccount_id=${MYACCOUNT_ID}&bodega_id=${user.bodegaId}&id_customer=${user.idCustomer}&search=${code}&solo_acuerdo=${isBMI(user)}`
      );
      const data = await res.json();

      if (data.length === 1) {
        const p = data[0];


        const subtotalCents = calcCents(p.unitPrice, qty);
        const st = (subtotalCents / 100).toFixed(2);

        const taxCalc = p.payTax
          ? (Math.round(subtotalCents * (user.porcentaje / 100)) / 100).toFixed(2)
          : "0.00";

        const newItem = {
          img: `https://www.ultra-erp.pro/products/${MYACCOUNT_ID}/${p.productId}.jpg`,
          idProduct: p.idProduct,
          code: p.productId,
          description: p.productName,
          price: p.unitPrice,
          qty,
          subtotal: st,
          payTax: p.payTax,
          tax: taxCalc,
          service: p.service,
        };

        const idx = items.length;
        setItems(prev => [...prev, newItem]);

        setInfoProduct({ ...newItem, index: idx });
        setInfoOpen(true);

        setCode("");
        setDescription("—");
        setQty(1);
        setPrice("—");
        setSubtotal("—");
        setTaxLine("0.00");
        setPayTaxFlag(false);
        setServiceFlag(0);
        setImgPreview(null);
        setIdProduct(null);

        return;
      }
    } catch (error) {
      console.error("Error buscando producto:", error);
    }
  };

  // ====================== SELECCIÓN DESDE MODAL ======================
  const handleProductSelect = (item) => {
    const exists = items.find((x) => x.idProduct === item.idProduct);

    const subtotalCents = calcCents(item.unitPrice, qty);
    const st = (subtotalCents / 100).toFixed(2);

    const taxCalc = item.payTax
      ? (Math.round(subtotalCents * (user.porcentaje / 100)) / 100).toFixed(2)
      : "0.00";

    if (exists) {
      const updated = items.map((x) => {
        if (x.code !== item.productId) return x;
        const newQty = x.qty + qty;
        const exCents = calcCents(x.price, newQty);
        return {
          ...x,
          qty: newQty,
          subtotal: (exCents / 100).toFixed(2),
          tax: x.payTax ? (Math.round(exCents * (user.porcentaje / 100)) / 100).toFixed(2) : "0.00",
        };
      });

      setItems(updated);

      const idx = updated.findIndex((x) => x.code === item.productId);
      setInfoProduct({ ...updated[idx], index: idx });
      setInfoOpen(true);
    } else {
      const newItem = {
        img: `https://www.ultra-erp.pro/products/${MYACCOUNT_ID}/${item.productId}.jpg`,
        idProduct: item.idProduct,
        code: item.productId,
        description: item.productName,
        notes: null,
        price: item.unitPrice,
        qty,
        subtotal: st,
        payTax: item.payTax,
        tax: taxCalc,
        service: item.service,
      };

      const idx = items.length;
      setItems(prev => [...prev, newItem]);
      setInfoProduct({ ...newItem, index: idx });
      setInfoOpen(true);
    }

    setModalOpen(false);

    setCode("");
    setDescription("—");
    setQty(1);
    setPrice("—");
    setSubtotal("—");
    setTaxLine("0.00");
    setPayTaxFlag(false);
    setServiceFlag(0);
    setImgPreview(null);
    setIdProduct(null);
  };

  // ====================== ELIMINAR ITEM ======================
  const removeItem = (code) => {
    setItems(prev => prev.filter((x) => x.code !== code));
  };

  // ====================== TOTALES ======================
  const validItems = items.filter((it) => !it.isError);

  const valorBruto = validItems
    .reduce((acc, it) => acc + Number(it.subtotal || 0), 0)
    .toFixed(2);
  const descuento = "0.00";
  const subTotal = (Number(valorBruto) - Number(descuento)).toFixed(2);
  const subTotal0 = validItems
    .filter((it) => !it.payTax)
    .reduce((acc, it) => acc + Number(it.subtotal || 0), 0)
    .toFixed(2);
  const gravado = validItems
    .filter((it) => it.payTax)
    .reduce((acc, it) => acc + Number(it.subtotal || 0), 0)
    .toFixed(2);
  const gravadoNoServ = validItems
    .filter((it) => it.payTax && it.service !== 1)
    .reduce((acc, it) => acc + Number(it.subtotal || 0), 0);
  const ivaTotal = (Math.round(gravadoNoServ * (user.porcentaje / 100) * 100) / 100).toFixed(2);

  const gravadoServ = validItems
    .filter((it) => it.service === 1)
    .reduce((acc, it) => acc + Number(it.subtotal || 0), 0);
  const ivaServicios = (Math.round(gravadoServ * (user.porcentaje / 100) * 100) / 100).toFixed(2);
  const totalFinal = (
    Number(gravado) +
    Number(subTotal0) +
    Number(ivaTotal) +
    Number(ivaServicios) -
    Number(descuento)
  ).toFixed(2);

  if (!user) {
    return (
      <div className="p-10 text-center text-red-600 font-bold">
        ❌ No hay usuario en sesión
      </div>
    );
  }
  // ============================ GUARDAR PEDIDO ============================
  const proceedCheckout = () => {
    const validItems = items.filter((x) => {
      return (
        (x.idProduct || x.productId) && // 👈 producto real
        !x.isError &&
        Number(x.qty) > 0
      );
    });
    if (validItems.length === 0) {
      alert("No hay productos en la Orden!");
      return;
    }

    const proformaSource = JSON.parse(localStorage.getItem("proforma_source") || "null");
    const payload = buildOrderPayload({
      items: validItems,
      user,
      claseOverride: isProformaMode ? " " : (!user?.changeCustomer && user?.groupId === "     1") ? "+" : "*",
      pedidoNumber: proformaSource?.pedidoNumber ?? null,
      crossId: proformaSource?.crossId ?? null,
      carNumber: proformaSource?.carNumber ?? null,
      noComps: proformaSource?.noComps ?? null,
    });
    localStorage.removeItem("proforma_source");

    localStorage.setItem("checkout_payload", JSON.stringify(payload));
    history.push(isProformaMode ? "/checkout?mode=proforma" : "/checkout");
  };
  // ✅ ESTA LÍNEA ES OBLIGATORIA
  const hasAccess = hasAccountAccess();
  // ✅ render condicional DESPUÉS de hooks
  if (!hasAccess) {
    return <Unauthorized />;
  }
  return (
    <div className="container mx-auto px-3 sm:px-12 py-6 mt-24 mb-10">
      {/* HEADER */}
      <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4 mb-6">

        {/* LEFT */}
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            onClick={() => history.goBack()}
            className="flex items-center gap-2 border px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-300 text-sm sm:text-base"
          >
            <FaArrowLeft />{t.btnBack}
          </button>

          <div className="flex items-end gap-3">
            <h1
              className="text-2xl sm:text-3xl font-bold px-3 py-1 rounded-lg"
              style={{
                backgroundColor: isProformaMode ? "#FFFFB3" : "#80FF80",
                color: "#000000",
              }}
            >
              {isProformaMode ? "Solicitud de Proforma" : t.menuQuickOrder}
            </h1>

            {!isProformaMode && (
              <span className="text-xs sm:text-sm font-semibold text-gray-600 tracking-wide uppercase">
                {t.defaultShoppingCart}
              </span>
            )}
          </div>
        </div>


        {/* RIGHT ACTIONS */}
        <div className="relative flex items-center gap-2 sm:gap-3">
          {/* More Options BUTTON */}
          <button
            onClick={() => setShowMoreOptions(true)}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 border rounded-lg text-sm hover:bg-gray-200"
          >
            <span className="text-lg">⋮</span>
            <span className="hidden sm:inline">{t.btnMoreOptions}</span>
          </button>

          {showMoreOptions && (
            <div
              className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-50"
            >
              <div className="p-2">

                <button
                  onClick={() => {
                    setShowMoreOptions(false);
                    setShowEmptyConfirm(true);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition"
                >
                  {t.empyCart}
                </button>

              </div>
            </div>
          )}




          {/* Help */}
          <button
            onClick={() => setShowHelp(true)}
            className="w-9 h-9 flex items-center justify-center border rounded-lg hover:bg-gray-200"
            title="Help"
          >
            ?
          </button>

          {/* Fullscreen */}
          <button
            onClick={toggleFullscreen}
            className="w-9 h-9 flex items-center justify-center border rounded-lg hover:bg-gray-200"
            title="Fullscreen"
          >
            ⛶
          </button>

        </div>
      </div>


      {/* ====================== TABLA PRINCIPAL ====================== */}
      <div className="bg-white shadow rounded-lg mb-6 overflow-hidden">
        <div className="overflow-y-auto overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-gray-100 border-b text-xs">
              <tr className="text-left align-middle">
                <th className="px-2 py-2 w-16 text-center"></th>
                <th className="px-2 py-2 w-24">Código</th>
                <th className="px-2 py-2">Descripción</th>
                <th className="px-2 py-2 w-16 text-center">Cantidad</th>
                <th className="px-2 py-2 w-20 text-right">Precio</th>
                <th className="px-2 py-2 w-16 text-right">IVA</th>
                <th className="px-2 py-2 w-24 text-right">Total</th>
                <th className="px-2 py-2 w-40 text-center">Acciones</th>
              </tr>
            </thead>

            <tbody>

              {/* ===================== PRODUCTOS VALIDOS ===================== */}
              {items.map((x, i) => {
                if (x.isError) return null;

                return (
                  <tr
                    key={i}
                    onClick={() => {
                      setInfoProduct({ ...x, index: i });
                      setInfoOpen(true);
                    }}
                    className="border-b hover:bg-gray-200 hover:shadow-lg transition duration-300 align-middle cursor-pointer"
                  >
                    <td className="px-2 py-1.5">
                      <img
                        src={x.img}
                        className="w-16 h-16 border rounded-md object-cover shadow-sm"
                        alt=""
                      />
                    </td>

                    <td className="px-2 py-1.5">{x.code}</td>
                    <td
                      className="px-2 py-1.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {editingNoteIdx === i ? (
                        <div className="flex flex-col gap-1">
                          <textarea
                            autoFocus
                            rows={2}
                            value={editingNoteValue}
                            onChange={(e) => setEditingNoteValue(e.target.value)}
                            className="w-full border rounded px-2 py-1 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary"
                          />

                          <div className="flex gap-1">
                            <button
                              onClick={() => {
                                setItems(prev =>
                                  prev.map(item =>
                                    item.code === x.code ? { ...item, notes: editingNoteValue } : item
                                  )
                                );
                                setEditingNoteIdx(null);
                              }}
                              className="px-3 py-1 bg-brand-primary text-white text-xs font-semibold rounded hover:brightness-90 transition"
                            >
                              Aceptar
                            </button>

                            <button
                              onClick={() => setEditingNoteIdx(null)}
                              className="px-3 py-1 border border-gray-300 text-gray-600 text-xs rounded hover:bg-gray-100 transition"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span
                            className="px-1 rounded"
                            style={x.notes && x.notes !== x.description ? { backgroundColor: "#00D2D2" } : {}}
                          >
                            {x.notes || x.description}
                          </span>

                          {user?.swWeb14 && (
                            <div className="relative group shrink-0">
                              <button
                                className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-gray-300 text-gray-500 hover:border-brand-primary hover:text-brand-primary hover:bg-brand-primary/10 transition-all text-xs"
                                onClick={() => {
                                  setEditingNoteValue(
                                    x.notes || x.description
                                  );

                                  setEditingNoteIdx(i);
                                }}
                              >
                                <FaPencilAlt size={10} />
                                <span>Editar</span>
                              </button>
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 pointer-events-none">
                                <div className="bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap shadow-lg">
                                  Clic para poder editar las caracteristcas de un nuevo producto
                                </div>
                                <div className="w-2 h-2 bg-gray-800 rotate-45 absolute left-1/2 -translate-x-1/2 -bottom-1" />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </td>

                    <td
                      className="px-2 py-1.5 text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="number"
                        min={1}
                        value={x.qty}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => {
                          const q = Math.max(1, Number(e.target.value));
                          const subtotalCents = calcCents(x.price, q);
                          const newSubtotal = (subtotalCents / 100).toFixed(2);

                          const newTax = x.payTax
                            ? (Math.round(subtotalCents * (user.porcentaje / 100)) / 100).toFixed(2)
                            : "0.00";

                          setItems(prev =>
                            prev.map((r, idx) =>
                              idx === i ? { ...r, qty: q, subtotal: newSubtotal, tax: newTax } : r
                            )
                          );
                        }}
                        className="w-20 border px-1 py-1 text-center rounded"
                      />
                    </td>

                    <td className="px-2 py-1.5 text-right">
                      {Number(x.price || 0).toFixed(4)}
                    </td>

                    <td className="px-2 py-1.5 text-right">
                      {x.tax > 0 ? "*" : ""}
                    </td>

                    <td className="px-2 py-1.5 text-right font-bold text-brand-primary">
                      {Number(x.subtotal || 0).toFixed(2)}
                    </td>

                    <td
                      className="px-2 py-1.5 text-center w-40"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-center gap-4">

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setInfoProduct({ ...x, index: i });
                            setInfoOpen(true);
                          }}
                        >
                          <FaInfoCircle className="text-gray-700 hover:text-black transition" size={20} />
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            alert("Aquí irá el modal de comentarios ✍️");
                          }}
                        >
                          <FaCommentDots className="text-gray-700 hover:text-black transition" size={20} />
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeItem(x.code);
                          }}
                        >
                          <FaTrash className="text-red-500 hover:text-red-700 transition" size={20} />
                        </button>

                      </div>
                    </td>
                  </tr>
                );
              })}

              {/* ===================== FILAS CON ERROR ===================== */}
              {items
                .filter((x) => x.isError)
                .map((x, idx) => (
                  <tr
                    key={`err-${idx}`}
                    className="bg-red-100 border-b border-red-300 align-middle"
                  >
                    <td className="px-2 py-1.5">
                      <div className="w-16 h-16 bg-red-200 border border-red-300 rounded flex items-center justify-center text-red-700 font-bold">
                        !
                      </div>
                    </td>

                    <td className="px-2 py-1.5">
                      <input
                        value={x.code}
                        onChange={(e) => {
                          const updated = [...items];
                          const i = updated.findIndex((r) => r === x);
                          if (i !== -1) {
                            updated[i].code = e.target.value;
                            setItems(updated);
                          }
                        }}
                        onKeyDown={async (e) => {
                          if (e.key !== "Enter") return;

                          const product = await fetch(
                            `${API_BASE}/inventary-ultra/api/ws/search_products_api?myaccount_id=${MYACCOUNT_ID}&bodega_id=${user.bodegaId}&id_customer=${user.idCustomer}&search=${x.code}&solo_acuerdo=${isBMI(user)}`
                          ).then((r) => r.json());

                          if (!product || product.length !== 1) return;

                          const p = product[0];

                          const subtotalCents = calcCents(p.unitPrice, x.qty);
                          const st = (subtotalCents / 100).toFixed(2);
                          const taxCalc = p.payTax
                            ? (Math.round(subtotalCents * (user.porcentaje / 100)) / 100).toFixed(2)
                            : "0.00";


                          const updated = [...items];
                          const i = updated.findIndex((r) => r === x);
                          if (i === -1) return;

                          updated[i] = {
                            img: `https://www.ultra-erp.pro/products/${MYACCOUNT_ID}/${p.productId}.jpg`,
                            idProduct: p.idProduct,
                            code: p.productId,
                            description: p.productName,
                            notes: null,
                            price: p.unitPrice,
                            qty: x.qty,
                            subtotal: st,
                            payTax: p.payTax,
                            tax: taxCalc,
                            service: p.service,
                          };

                          setItems(updated);
                        }}
                        className="w-24 border border-red-400 px-2 py-1 rounded text-red-700"
                      />
                    </td>

                    <td className="px-2 py-1.5 text-red-700 italic">
                      {x.errorReason || "Producto no encontrado"}
                    </td>

                    <td className="px-2 py-1.5 text-center">
                      <input
                        type="number"
                        min={1}
                        value={x.qty}
                        onChange={(e) => {
                          const updated = [...items];
                          const i = updated.findIndex((r) => r === x);
                          if (i !== -1) {
                            updated[i].qty = Math.max(1, Number(e.target.value));
                            setItems(updated);
                          }
                        }}
                        className="w-12 border border-red-400 px-1 py-1 text-center rounded text-red-700"
                      />
                    </td>

                    <td className="px-2 py-1.5 text-right text-red-400">—</td>
                    <td className="px-2 py-1.5 text-right text-red-400">—</td>
                    <td className="px-2 py-1.5 text-right text-red-500 font-bold">
                      ERROR
                    </td>

                    <td className="px-2 py-1.5 text-center w-40">
                      <button
                        onClick={() => removeItem(x.code)}
                        className="text-red-600 hover:text-red-800"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}

              {/* ===================== FILA DEFAULT (SIEMPRE LA ULTIMA) ===================== */}
              <tr className="bg-gray-50 border-t align-middle">
                <td className="px-2 py-1.5">
                  {imgPreview ? (
                    <img
                      src={imgPreview}
                      className="w-16 h-16 border rounded object-cover"
                      alt=""
                    />
                  ) : (
                    <div className="w-16 h-16 border rounded bg-gray-200 flex items-center justify-center text-[10px] text-gray-600"></div>
                  )}
                </td>

                <td className="px-2 py-1.5">
                  <div className="flex">
                    <input
                      type="text"
                      value={code}
                      placeholder="Código"
                      onChange={(e) => setCode(e.target.value)}
                      onBlur={findProductByCode}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          findProductByCode();
                        }
                      }}
                      onPaste={async (e) => {
                        const pastedText = e.clipboardData.getData("text");

                        if (pastedText.includes("\n")) {
                          e.preventDefault();

                          const { nextItems, bulkErrors } = await handleBulkPaste({
                            rawText: pastedText,
                            items,
                            user,
                            myaccountId: MYACCOUNT_ID,
                          });

                          setItems(nextItems);
                          setBulkErrors(bulkErrors);
                        }
                      }}
                      className="w-24 border px-2 py-1 rounded-l
                            focus:outline-none
                            focus:border-brand-primary
                            focus:ring-1 focus:ring-brand-primary"
                    />

                    <button
                      onClick={() => setModalOpen(true)}
                      className="px-3 bg-gray-300 hover:bg-gray-400 rounded-r border-l"
                      title="Buscar producto"
                    >
                      <FaSearch size={14} />
                    </button>
                  </div>
                </td>

                <td className="px-2 py-1.5">{description}</td>

                <td className="px-2 py-1.5 text-center">
                  <input
                    type="number"
                    min={1}
                    value={qty}
                    onChange={(e) =>
                      setQty(Math.max(1, Number(e.target.value)))
                    }
                    className="w-12 border px-1 py-1 text-center rounded"
                  />
                </td>

                <td className="px-2 py-1.5 text-right">
                  {price !== "—" ? price : "—"}
                </td>

                <td className="px-2 py-1.5 text-right text-green-600"></td>

                <td className="px-2 py-1.5 text-right font-semibold">
                  {subtotal > 0.0 ? "*" : "—"}
                </td>

                <td className="px-2 py-1.5 text-center w-40"></td>
              </tr>

            </tbody>



          </table>
        </div>
      </div>

      {bulkErrors.length > 0 && (
        <div className="mt-4 bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-700 font-semibold mb-2">
            ⚠️ Algunos productos no se pudieron cargar
          </p>

          <div className="max-h-40 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-red-600 border-b">
                  <th className="py-1">Código</th>
                  <th className="py-1">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {bulkErrors.map((err, idx) => (
                  <tr key={idx} className="border-b last:border-0">
                    <td className="py-1 font-mono text-red-700">{err.code}</td>
                    <td className="py-1 text-red-600">{err.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ====================== TOTALES DETALLADOS ====================== */}
      <div className="flex justify-center md:justify-end mt-4">
        <div className="w-full max-w-xs sm:max-w-sm md:w-80 bg-white shadow rounded-lg p-5 md:p-6 md:mr-[130px]">
          <div className="flex justify-between mb-2 text-sm">
            <span className="font-medium">SubTotal</span>
            <span className="font-bold">USD {subTotal}</span>
          </div>

          <div className="flex justify-between mb-2 text-sm">
            <span className="font-medium">IVA</span>
            <span className="font-bold">USD {(Number(ivaTotal) + Number(ivaServicios)).toFixed(2)}</span>
          </div>

          <div className="flex justify-between mb-4 text-base font-bold">
            <span>TOTAL</span>
            <span className="text-brand-primary text-lg">USD {totalFinal}</span>
          </div>

          <button
            className={`
              w-full py-3 rounded-lg
              text-base sm:text-lg font-semibold
              hover:brightness-90 transition
              flex items-center justify-center gap-2 text-black
              ${isProformaMode
                ? "bg-[#FFFFB3]"
                : "bg-[#80FF80]"
              }
            `}
            onClick={proceedCheckout}
          >
            <FaShoppingCart className="text-xl" />
            {isProformaMode ? "Procesar Solicitud de Proforma" : t.btnProcedToCheckout}
          </button>
        </div>
      </div>

      {/* ====================== MODALES ====================== */}
      <ProductSearchModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSelect={handleProductSelect}
      />

      <ProductInfoModal
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        product={infoProduct}
        onQtyChange={updateItemFromModal}
        user={user}
      />
      {showEmptyConfirm && (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-start justify-center">
          <div className="mt-24 bg-black text-white px-10 py-6 rounded-xl shadow-2xl min-w-[420px] text-center">

            <p className="mb-6 text-sm">
              {t.confEmpyCart}
            </p>

            <div className="flex justify-center gap-6">
              {/* YES */}
              <button
                onClick={() => {
                  clearCart();
                  setShowEmptyConfirm(false);
                }}
                className="flex items-center gap-2 px-6 py-2 border border-red-500 rounded-md text-red-400 hover:bg-red-500/10"
              >
                ✓ {t.yes}
              </button>

              {/* NO */}
              <button
                onClick={() => setShowEmptyConfirm(false)}
                className="flex items-center gap-2 px-6 py-2 border border-green-500 rounded-md text-green-400 hover:bg-green-500/10"
              >
                ✕ No
              </button>
            </div>

          </div>
        </div>
      )}

    </div>


  );
}
